/**
 * Optional mount points for the Flows feature (owned by the flows agent in `src/features/flows/**`, which exports
 * `FlowCard`, `FlowPreviewPanel` and `FlowEditorPage`). Loaded through `import.meta.glob` so the app still builds and
 * degrades gracefully (simple PlayAd-style card + "Open editor" link) until that folder exists.
 */
import { ChevronRight, ExternalLink, Workflow, X } from "lucide-react";
import { lazy, Suspense, type ComponentType, type ReactNode } from "react";
import { Link } from "react-router";
import { cn } from "./cn";

interface FlowsModule {
  FlowCard?: ComponentType<Record<string, unknown>>;
  FlowPreviewPanel?: ComponentType<Record<string, unknown>>;
  FlowEditorPage?: ComponentType<Record<string, unknown>>;
  default?: unknown;
}

const loaders = import.meta.glob<FlowsModule>("../features/flows/index.{ts,tsx}");
const loader = Object.values(loaders)[0] ?? null;

export const flowsAvailable = loader !== null;

function lazyExport(name: keyof FlowsModule, Fallback: ComponentType<Record<string, unknown>>) {
  return lazy(async () => {
    if (!loader) return { default: Fallback };
    try {
      const m = await loader();
      const C = m[name] as ComponentType<Record<string, unknown>> | undefined;
      return { default: C ?? Fallback };
    } catch {
      return { default: Fallback };
    }
  });
}

/** Flow editor URL for a brand's flow. */
export function flowEditorPath(brandId: string | null | undefined, flowId: string): string {
  return brandId ? `/b/${encodeURIComponent(brandId)}/flows/${encodeURIComponent(flowId)}` : `/flows/${encodeURIComponent(flowId)}`;
}

// ─── Fallbacks ───────────────────────────────────────────────────────────────

function FallbackCard(props: Record<string, unknown>) {
  const title = (props.title as string) ?? "Flow";
  const status = props.status as string | undefined;
  const onOpen = props.onOpen as (() => void) | undefined;
  const href = flowEditorPath(props.brandId as string | undefined, String(props.flowId ?? props.id ?? ""));
  const body = (
    <>
      <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary-soft text-lavender">
        <Workflow className="size-4.5" strokeWidth={1.75} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13.5px] font-semibold">{title}</span>
        <span className="block text-[12px] text-muted">
          Open flow • preview and export{status ? ` · ${status}` : ""}
        </span>
      </span>
      <ChevronRight className="size-4 text-muted" />
    </>
  );
  const cls = "flex w-full max-w-md items-center gap-3 rounded-xl border border-primary/70 bg-surface px-3 py-2.5 text-left transition-colors hover:bg-hover";
  return onOpen ? (
    <button type="button" onClick={onOpen} className={cls}>
      {body}
    </button>
  ) : (
    <Link to={href} className={cls}>
      {body}
    </Link>
  );
}

function FallbackPanel(props: Record<string, unknown>) {
  const onClose = props.onClose as (() => void) | undefined;
  const href = flowEditorPath(props.brandId as string | undefined, String(props.flowId ?? ""));
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-line px-4 py-3">
        <span className="truncate text-[14px] font-semibold">{(props.title as string) ?? "Flow"}</span>
        <div className="flex items-center gap-1">
          <Link to={href} className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-primary px-3 text-[12.5px] font-medium text-white hover:bg-primary-hover">
            Open editor <ExternalLink className="size-3.5" />
          </Link>
          {onClose && (
            <button type="button" onClick={onClose} aria-label="Close" className="grid size-8 place-items-center rounded-lg text-muted hover:bg-hover hover:text-fg">
              <X className="size-4" />
            </button>
          )}
        </div>
      </div>
      <div className="grid flex-1 place-items-center p-6 text-center text-[13px] text-muted">The live flow preview isn't available in this build — open the editor to see the canvas.</div>
    </div>
  );
}

function FallbackEditor() {
  return (
    <div className="grid min-h-[60vh] place-items-center p-8 text-center">
      <div>
        <Workflow className="mx-auto mb-3 size-8 text-muted" strokeWidth={1.5} />
        <div className="text-[15px] font-semibold">Flow editor unavailable</div>
        <p className="mt-1 text-[13px] text-muted">The Flows module isn't part of this build yet.</p>
      </div>
    </div>
  );
}

const LazyCard = lazyExport("FlowCard", FallbackCard);
const LazyPanel = lazyExport("FlowPreviewPanel", FallbackPanel);
const LazyEditor = lazyExport("FlowEditorPage", FallbackEditor as ComponentType<Record<string, unknown>>);

function Slot({ children, fallback, className }: { children: ReactNode; fallback?: ReactNode; className?: string }) {
  return (
    <Suspense fallback={fallback ?? <div className={cn("h-14 w-full max-w-md animate-pulse rounded-xl bg-elevated/60", className)} />}>{children}</Suspense>
  );
}

/** Chat flow card (attachment kind "flow"): `{ flowId, brandId, title, status, onOpen }` passed through. */
export function FlowCardSlot(props: Record<string, unknown>) {
  return (
    <Slot>
      <LazyCard {...props} />
    </Slot>
  );
}

/** Right side panel with the live flow preview: `{ flowId, brandId, title, onClose, onOpenEditor }` passed through. */
export function FlowPanelSlot(props: Record<string, unknown>) {
  return (
    <Slot fallback={<div className="h-full animate-pulse bg-surface" />}>
      <LazyPanel {...props} />
    </Slot>
  );
}

/** Route element for `/b/:brandId/flows/:flowId`. */
export function FlowEditorRoute(props: Record<string, unknown>) {
  return (
    <Slot fallback={<div className="grid min-h-[60vh] place-items-center text-muted">Loading editor…</div>}>
      <LazyEditor {...props} />
    </Slot>
  );
}
